<script setup>
/**
 * 控制台主布局：侧栏 + 顶栏 + 可滚主区。
 *
 * 从 App.vue 拆出来，是为了让"运维诊断"这类页面能走另一套布局
 * （BlankLayout）——那种页面在后端连不上时也要能打开，
 * 不该被依赖后端数据的顶栏和侧栏拖住。
 */
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
// 顶栏动作按钮用 Element Plus 自带图标，与组件库视觉语言一致；
// 业务语义图标（导航、指标）仍用 Font Awesome。
import { VideoPlay, VideoPause, CircleClose, SwitchButton } from '@element-plus/icons-vue'
import { NAV_ITEMS } from '@/router'
import { stateLabel } from '@/utils/rules'
import { useConsoleStore } from '@/stores/console'
import { usePlanStore } from '@/stores/plan'
import { useRiskStore } from '@/stores/risk'
import StateTag from '@/components/StateTag.vue'
import ExchangeSwitcher from '@/components/ExchangeSwitcher.vue'

const route = useRoute()
const router = useRouter()
const c = useConsoleStore()
const plan = usePlanStore()
const risk = useRiskStore()

const connClass = computed(() => (!c.connected ? 'down' : c.stale ? 'stale' : 'live'))
const connText = computed(() =>
  !c.connected ? '后端未连接' : c.stale ? '数据滞后' : '实时数据',
)

const envLabel = computed(() => {
  if (!c.connected) return '未连接'
  const env = c.environment === 'production' ? '生产' : '沙盒'
  return `${env}环境${c.hasCredentials ? '' : '（未配密钥）'}`
})

/* 环境标签连点三次进入诊断页。
 *
 * 诊断页刻意不做可见入口（运维动作，不属于交易员动线），
 * 但只能靠手输 URL 太不方便 —— 这里留一个不显眼的后门。
 * 相邻两次点击超过 CLICK_GAP_MS 就重新计数，避免平时误触累计触发。 */
const CLICK_GAP_MS = 1200
const CLICKS_REQUIRED = 3
const clickCount = ref(0)
const lastClickAt = ref(0)

function onEnvClick() {
  const now = Date.now()
  clickCount.value = now - lastClickAt.value > CLICK_GAP_MS ? 1 : clickCount.value + 1
  lastClickAt.value = now
  if (clickCount.value >= CLICKS_REQUIRED) {
    clickCount.value = 0
    router.push('/diagnostics')
  }
}

async function onStart() {
  if (risk.latched) {
    return ElMessage.error(`「${stateLabel(risk.botState)}」状态需人工解除后才能启动`)
  }
  if (!plan.approved) return ElMessage.error('盘前计划未批准，不得启动')
  if (plan.expired) return ElMessage.error('盘前计划已过期，不得启动')
  if (!plan.noTradeAllPass) {
    return ElMessage.error(`还有 ${plan.noTradeFailedCount} 项开仓条件未通过`)
  }
  risk.start()

  /* 启动 ≠ 会开仓。
   *
   * 启动只是把机器人置为"在岗"；每一次要不要买，仍然由 8 项风控检查逐次判定。
   * 之前这里只说一句"已启动"，检查不通过时人看不出差别，会以为按钮坏了。
   * 现在把"启动了但还不会买、卡在哪几项"直接说出来。 */
  const blocked = risk.checks.filter((x) => !x.pass)
  if (blocked.length) {
    ElMessage.warning(
      `机器人已启动，但暂不会开仓 —— ${blocked.length} 项风控检查未通过：` +
        blocked.map((x) => x.name).join('、'),
    )
  } else {
    ElMessage.success('机器人已启动，风控检查全部通过')
  }
}

function onPause() {
  if (!risk.running) return ElMessage.warning('机器人尚未启动')
  risk.pause()
  ElMessage.warning('已暂停')
}

async function onCancelAll() {
  if (!c.connected) return ElMessage.error('后端未连接')
  if (!c.enableActions) {
    return ElMessage.error('后端为只读模式，需以 --enable-actions 启动才能撤单')
  }
  try {
    await ElMessageBox.confirm(
      '将撤销交易所上该交易对的全部未成交挂单，确认执行？',
      '一键撤单',
      { type: 'warning', confirmButtonText: '确认撤单', cancelButtonText: '取消' },
    )
  } catch {
    return
  }
  try {
    const res = await c.cancelAll()
    risk.log('一键撤单', `请求 ${res.requested} 笔，已撤 ${res.cancelled} 笔`)
    ElMessage.success(`已撤销 ${res.cancelled} 笔挂单`)
  } catch (e) {
    ElMessage.error('撤单失败：' + (e.message || e))
  }
}

async function onProtect() {
  try {
    await ElMessageBox.confirm(
      '进入保护模式将停止当日全部主动交易，且不会自行恢复，须人工确认处置后才能解除。确认执行？',
      '保护模式',
      { type: 'error', confirmButtonText: '进入保护模式', cancelButtonText: '取消' },
    )
  } catch {
    return
  }
  risk.protect()
  ElMessage.error('已进入保护模式')
}
</script>

<template>
  <div class="app">
    <!-- 侧栏（宽屏） -->
    <aside class="side">
      <div class="brand">
        <div class="coin"><FaIcon :icon="['fab', 'bitcoin']" /></div>
        <div>
          <b>NexusBeta</b>
          <div class="brand-sub">BTC 现货低买高卖机器人</div>
        </div>
      </div>

      <nav class="side-nav">
        <RouterLink
          v-for="item in NAV_ITEMS"
          :key="item.path"
          :to="item.path"
          class="nav-item"
          :class="{ active: route.path === item.path }"
        >
          <FaIcon :icon="['fas', item.icon]" />
          <span>{{ item.title }}</span>
        </RouterLink>
      </nav>

      <div class="side-foot">
        <!-- 连点三次进诊断页。不加任何样式提示是有意的：
             诊断属于运维动作，不该出现在交易员的动线里，
             但出问题时又要有办法进得去。 -->
        <span @click="onEnvClick">{{ envLabel }}</span><br />
        <span class="mono" style="font-size: 0.6875rem">V1.2 规则基线</span>
      </div>
    </aside>

    <!-- 右侧内容列：固定顶栏 + 可滚主区 -->
    <div class="content">
      <header class="topbar">
        <!-- 状态与连接常驻顶栏：状态决定此刻允许哪些动作，
             和右侧的操作按钮放在同一行，按之前先看状态再动手 -->
        <div class="topbar-status">
          <StateTag :state="risk.botState" />
          <!-- 在岗与否是最该常驻的一条状态，之前整个界面都没显示过：
               点了启动只弹一句提示，看着像按钮没反应。
               「在岗」不等于「会开仓」，所以顺带把卡住的检查项数写出来。 -->
          <el-tooltip placement="bottom-start" :show-after="150">
            <template #content>
              <template v-if="!risk.running">机器人未启动，不会执行任何计划动作</template>
              <template v-else-if="risk.newBuyAllowed">风控检查全部通过，允许按计划新增买入</template>
              <template v-else>
                已启动，但风控检查未全通过，暂不开仓：<br />
                {{ risk.checks.filter((x) => !x.pass).map((x) => x.name).join('、') }}
              </template>
            </template>
            <span class="run" :class="risk.running ? (risk.newBuyAllowed ? 'on' : 'idle') : 'off'">
              <i class="led" />
              {{ risk.running ? (risk.newBuyAllowed ? '运行中' : '在岗·不开仓') : '未启动' }}
            </span>
          </el-tooltip>
          <!-- 交易所是全局上下文：界面上每个数字都属于某一家，必须常驻可见 -->
          <ExchangeSwitcher />
          <!-- 连接状态本身是全局的，之前每个页面顶部弹一条告警既占地方又重复；
               这里带上失败原因，鼠标悬停可看详情 -->
          <el-tooltip
            v-if="!c.connected"
            placement="bottom-start"
            :show-after="150"
          >
            <template #content>
              {{ c.error || '无法连接后端' }}<br />
              启动命令：python tools/api_server.py
            </template>
            <span class="conn down"><i class="led" />后端未连接</span>
          </el-tooltip>
          <span v-else class="conn" :class="connClass"><i class="led" />{{ connText }}</span>
        </div>

        <div class="actions">
          <el-button type="primary" :icon="VideoPlay" @click="onStart">
            <span>启动</span>
          </el-button>
          <el-button :icon="VideoPause" @click="onPause">
            <span>暂停</span>
          </el-button>
          <el-button :icon="CircleClose" @click="onCancelAll">
            <span>一键撤单</span>
          </el-button>
          <el-button type="danger" :icon="SwitchButton" @click="onProtect">
            <span>保护模式</span>
          </el-button>
        </div>
      </header>

      <main class="main">
        <!-- 后端连接状态已移到顶栏（全局状态，不该每页重复一条告警）。
             这条保留告警形态：风控失效属于必须立刻处理的情况，
             而且它指向一个具体的补救动作。 -->
        <div
          v-if="c.connected && !c.pnlTrustworthy && !plan.demo.on"
          class="banner warn"
          style="margin-bottom: 0.75rem"
        >
          <FaIcon :icon="['fas', 'triangle-exclamation']" />
          <span class="banner-text">
            <b>净盈亏暂无可信输入，亏损阈值未生效</b>
            <span class="sep">·</span>
            {{ c.pnlNote }}<template v-if="c.pnlRemedy">，{{ c.pnlRemedy }}</template>
          </span>
        </div>

        <slot />
      </main>
    </div>

    <!-- 底部导航（窄屏） -->
    <nav class="bottom-nav">
      <RouterLink
        v-for="item in NAV_ITEMS"
        :key="item.path"
        :to="item.path"
        :class="{ active: route.path === item.path }"
      >
        <FaIcon :icon="['fas', item.icon]" />
        <span>{{ item.short }}</span>
      </RouterLink>
    </nav>
  </div>
</template>

<style scoped>
.app {
  display: grid;
  grid-template-columns: var(--side-w) 1fr;
  width: 100vw;
  height: 100vh;
  height: 100dvh; /* 移动端地址栏收放时 dvh 才是真实可视高度 */
  overflow: hidden;
}

/* --- 侧栏 --- */
.side {
  border-right: var(--hair) solid var(--line);
  padding: 1.375rem 0.875rem;
  display: flex;
  flex-direction: column;
  background: #0d1117;
  overflow-y: auto;
  min-height: 0;
}
.brand { display: flex; gap: 0.6875rem; align-items: center; margin-bottom: 1.625rem; padding: 0 0.25rem; }
.brand b { font-size: 1rem; }
.brand-sub { color: var(--muted); font-size: 0.6875rem; margin-top: 0.125rem; }
.coin {
  width: 2.5rem; height: 2.5rem; border-radius: var(--radius); flex: none;
  background: var(--amber); display: grid; place-items: center;
  color: #171109; font-size: 2rem;
}

.side-nav { display: flex; flex-direction: column; gap: 0.25rem; }
.nav-item {
  display: flex; align-items: center; gap: 0.6875rem;
  padding: 0.625rem 0.75rem; border-radius: var(--radius-sm);
  color: var(--muted); text-decoration: none; font-size: 0.875rem;
}
.nav-item :deep(svg) { width: 1rem; }
.nav-item:hover { background: var(--panel); color: var(--text); }
.nav-item.active { background: var(--panel); color: var(--text); box-shadow: inset 0.125rem 0 0 var(--amber); }

.side-foot {
  margin-top: auto; border-top: var(--hair) solid var(--line); padding-top: 0.875rem;
  color: var(--muted); font-size: 0.75rem; line-height: 1.8;
}
/* --- 连接指示 --- */
/* 与 .state-tag 同高同行高，两者在顶栏并排时才不会一高一低 */
.conn {
  display: inline-flex; align-items: center; gap: 0.375rem;
  height: 1.5rem; line-height: 1;
  font-size: 0.6875rem; color: var(--muted);
}
.conn .led { width: 0.5rem; height: 0.5rem; border-radius: 50%; background: var(--muted); }
.conn.live .led { background: var(--green); box-shadow: 0 0 0.375rem var(--green); }
.conn.stale .led { background: var(--amber); }
/* 断连时整条变红并呼吸：顶栏这一处是唯一的连接提示，不能太安静 */
.conn.down { color: var(--red); font-weight: 700; cursor: help; }
.conn.down .led { background: var(--red); animation: pulse 1.4s ease-in-out infinite; }

/* 在岗状态。三档而不是两档：
   「运行中」= 会按计划买卖；「在岗·不开仓」= 已启动但风控没放行。
   这两者混成一个"运行中"，正是让人以为按钮没生效的原因。 */
.run {
  display: inline-flex; align-items: center; gap: 0.375rem;
  height: 1.5rem; line-height: 1;
  padding: 0 0.4375rem;
  border: var(--hair) solid var(--line);
  border-radius: var(--radius-sm);
  font-size: 0.6875rem; color: var(--muted); cursor: help;
}
.run .led { width: 0.5rem; height: 0.5rem; border-radius: 50%; background: var(--muted); }
.run.on { color: var(--green); border-color: var(--green); }
.run.on .led { background: var(--green); box-shadow: 0 0 0.375rem var(--green); }
.run.idle { color: var(--amber); border-color: var(--amber); }
.run.idle .led { background: var(--amber); }

@keyframes pulse {
  0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(255, 99, 115, 0.5); }
  50% { opacity: 0.55; box-shadow: 0 0 0 0.25rem rgba(255, 99, 115, 0); }
}
@media (prefers-reduced-motion: reduce) {
  .conn.down .led { animation: none; }
}

/* --- 内容列 --- */
.content { display: flex; flex-direction: column; min-width: 0; min-height: 0; overflow: hidden; }

/* 顶栏用 flex:none 参与布局而不是 position:fixed —— 天然不滚动，
   也不必给主区手动留等高的 padding（改高度就错位）。 */
.topbar {
  flex: none;
  display: flex; align-items: center; justify-content: space-between;
  gap: 1rem; flex-wrap: wrap;
  padding: 0.875rem 1.625rem;
  background: var(--panel);
  border-bottom: var(--hair) solid var(--line);
}
.topbar-status { display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap; }
.actions { display: flex; gap: 0.5rem; flex-wrap: wrap; }
.actions :deep(.el-button) { display: inline-flex; align-items: center; gap: 0.4375rem; margin-left: 0; }

.main {
  flex: 1;
  padding: 1.5rem 1.625rem;
  overflow-y: auto;   /* 唯一的纵向滚动容器 */
  overflow-x: hidden; /* 宽内容由表格自身处理 */
  min-height: 0;
}

/* --- 底部导航（窄屏） --- */
.bottom-nav { display: none; }

@media (max-width: 45rem) {
  .app { display: flex; flex-direction: column; }
  .side { display: none; }

  .topbar { padding: 0.625rem 0.875rem; gap: 0.625rem; }
  /* 窄屏侧栏隐藏，状态更要留在顶栏；单独占一行避免和按钮挤在一起 */
  .topbar-status { width: 100%; justify-content: space-between; }
  .actions { width: 100%; gap: 0.375rem; }
  .actions :deep(.el-button) { flex: 1 1 0; min-width: 0; padding: 0.5rem 0.25rem; font-size: 0.75rem; }

  .main { flex: 1; padding: 1rem 0.875rem calc(var(--bottom-nav-h) + env(safe-area-inset-bottom, 0px) + 1rem); }

  .bottom-nav {
    display: flex;
    position: fixed; left: 0; right: 0; bottom: 0; z-index: 40;
    height: calc(var(--bottom-nav-h) + env(safe-area-inset-bottom, 0px));
    padding-bottom: env(safe-area-inset-bottom, 0px);
    background: #0d1117; border-top: var(--hair) solid var(--line);
  }
  .bottom-nav a {
    flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center;
    gap: 0.1875rem; font-size: 0.625rem; color: var(--muted); text-decoration: none;
  }
  .bottom-nav a :deep(svg) { font-size: 1.0625rem; }
  .bottom-nav a.active { color: var(--amber); }
}

@media (max-width: 26.25rem) {
  .actions :deep(.el-button) span { display: none; }
  .bottom-nav a { font-size: 0.5625rem; }
}
</style>
